import { Alert, Button, Card, Modal, OverlayTrigger, Table, Tooltip } from "react-bootstrap"
import { DRImportFieldType, DRImportSectionType } from "./DRImportConfig"
import { useAppSelector } from "../../../redux/hooks";
import { RPWDataReader, getDataReader } from "../../../livedataFuncs";
import React, { useState } from "react";
import moment from "moment";
import { validateRequiredFields } from "./DRImportFuncs";
import { isPageInDR } from "../../../dr/Components/drFuncs";
import { NavObjectType } from "../../navigationTypes";
import { runInnerFunction } from "../../../compatManager";


type BuilderImportFieldRowType = {
    label: string,
    from: string,
    to: string,
    setter?: (...args) => void,
}

function getInfileValue(field: DRImportFieldType, val: string) {
    if (!val || val === "") return "no data";
    if (field.type === "number") {
        return "$" + Number(val).toLocaleString();
    }
    if (field.type === "date") {
        return moment(val).format('D/MMM/YYYY');
    }
    if (field.type === "boolean") {
        return val === "1" ? "Yes" : "No";
    }
    return val;
}

const DRImportCard = ({secName, secConfig, drDataRoot} : {secName: string, secConfig: DRImportSectionType, drDataRoot: any}) => {
    const page: NavObjectType = useAppSelector((state) => state.currentNavigation.page);
    const livedataRoot: any = useAppSelector((state) => state.currentNavigation.livedataRootContainer?.dataRoot);
    const processor: RPWDataReader = getDataReader(livedataRoot);
    const drProcessor: RPWDataReader = getDataReader(drDataRoot);
    const [showBuyerImportAlert, setShowBuyerImportAlert] = useState(false);

    const onClickImport = async (setter: (...args: any[]) => void, secConfig?: DRImportSectionType) => {
        if (!setter && secConfig?.importAll) {
            // import all from sec
            const fields = secConfig.fields.filter(field => validateRequiredFields(field, processor, drProcessor));
            fields.forEach(field => {
                if (field.setter) {
                    field.setter(drProcessor, processor);
                }
            })
        } else {
            setter(drProcessor, processor);
        }
        runInnerFunction('CalculateContentHolder', null);
        runInnerFunction('UpdateContentHolder', null);
    }

    
    const BuilderImportFieldRow = (rowProps: BuilderImportFieldRowType) => {
        let icon;
        if (rowProps.from === rowProps.to) {
            icon = <i className="fa-regular fa-circle-arrow-right" style={{ color: "#AAA7A7", pointerEvents: "none"}} />
        } else {
            icon = <OverlayTrigger placement="bottom" delay={100} overlay={
                <Tooltip>Import to File</Tooltip>
            }>
                <i className="fa-solid fa-circle-arrow-right" onClick={() => onClickImport(rowProps.setter)} />
            </OverlayTrigger>
        }
        return <tr>
            <td className="dr-row-label">{rowProps.label}</td>
            <td className={rowProps.from === "no data" ? `dr-row-no-data` : ``}>{rowProps.from}</td>
            {!isPageInDR(page) && (<td className={rowProps.to === "no data" ? `dr-row-no-data` : ``}>{rowProps.to}</td>)}
            {!isPageInDR(page) && <td>{rowProps.setter && icon}</td>}
        </tr>
    }

    function buildRow(secName: string, secConfig: DRImportSectionType) {
        if (secConfig.isLoop) {
            const jsxElements = [];
            let fromNodes, toNodes;
            switch (secConfig.loopOn) {
                case "Purchaser":
                    fromNodes = drProcessor.getBuyerPeopleInfo();
                    toNodes = processor.getBuyerPeopleInfo();
                    break;
                case "Deposit":
                    fromNodes = drProcessor.getDeposits();
                    toNodes = processor.getDeposits();
                    break;
            }
            const maxLen = Math.max(fromNodes?.length, toNodes?.length);
            if (maxLen > 0) {
                for (let i = 0; i < maxLen; i++) {
                    const name = fromNodes[i]?.Name ?? toNodes[i]?.Name;
                    if (secConfig.loopOn === "Purchaser") {
                        jsxElements.push(<tr key={"buyer"+i}><th colSpan={4} style={{textAlign: 'center'}} >Buyer - {name}</th></tr>)
                    }
                    // loop on field
                    jsxElements.push(secConfig.fields.filter(element => validateRequiredFields(element, processor, drProcessor)).map((field, index) => {
                        let label = field.isBrokendown ? `    - ${field.label}` : field.label;
                        if (secConfig.loopOn === "Deposit") label += ` ${i + 1}`;
                        return <BuilderImportFieldRow 
                            setter={field.setter}
                            label={label}
                            from={getInfileValue(field, field.src(fromNodes[i]))}
                            to={getInfileValue(field, field.src(toNodes[i]))}
                            key={secName + index} />
                    }));
                }
            } else {
                jsxElements.push(<tr>
                    <td className="dr-row-label"></td>
                    <td className="dr-row-no-data">no data</td>
                    {!isPageInDR(page) &&
                    <>
                    <td className="dr-row-no-data">no data</td>
                    <td></td>
                    </>}
                </tr>)
            }
            return jsxElements;
        } else {
            return <React.Fragment key={`${secName}-import-template`}>
                {secConfig.fields.filter(element => validateRequiredFields(element, processor, drProcessor)).map((field, index) => {
                    let label = field.isBrokendown ? `    - ${field.label}` : field.label;
                    return <BuilderImportFieldRow 
                        setter={field.setter}
                        label={label}
                        from={getInfileValue(field, field.src(drProcessor, drDataRoot))}
                        to={getInfileValue(field, field.src(processor, livedataRoot))}
                        key={secName + index} />
                })}
            </React.Fragment>
        }
    }

    return (
        <>
            <Card className="card-list">
                <Card.Header>
                    <h2>{secName}</h2>
                    {(secConfig.setter || secConfig.importAll) && !isPageInDR(page) &&
                        <Button
                            variant="outline-primary"
                            onClick={() => {
                                switch (secName) {
                                    case "Buyer":
                                        setShowBuyerImportAlert(true);
                                        break;
                                    default:
                                        onClickImport(secConfig.setter, secConfig)
                                        break;
                                }
                            }}>
                            <i className="fa-solid fa-circle-arrow-right"/>
                            Import {secName}
                        </Button>
                    }
                </Card.Header>

                <Card.Body style={{whiteSpace: "break-spaces"}}>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th style={{width:'20%'}}></th>
                                <th style={{width: isPageInDR(page) ? '80%' : '35%'}}>From Seller</th>
                                {!isPageInDR(page) && <>
                                    <th style={{width:'35%'}}>In File</th>
                                    <th style={{width:'5%'}}></th>
                                </>}
                            </tr>
                        </thead>
                        <tbody>
                            {buildRow(secName, secConfig)}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <Modal
                show={showBuyerImportAlert}
                onHide={() => setShowBuyerImportAlert(false)}
                size="sm"
                backdrop="static"
                keyboard={false}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Warning</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning">This will delete all of your current buyer's information in this file.</Alert>
                    <p>Are you sure you want to proceed?</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowBuyerImportAlert(false)}>Cancel</Button>
                    <Button variant="primary" onClick={() => {onClickImport(secConfig.setter, secConfig);setShowBuyerImportAlert(false)}}>Yes</Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default DRImportCard;